import React, { useState, useMemo, useEffect } from 'react';
import { Button, Row, Col, Card, Modal, Form } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import { jsPDF } from 'jspdf';
import { trackEvent, trackFirstTime, trackPageView } from '../utils/analytics';

const SOW_DOC_ID = '__sow__';

const IMAGE_LINE_REGEX = /^!\[([^\]]*)\]\(([^)\s]+)\)\s*$/;
const IMAGE_GLOBAL_REGEX = /!\[([^\]]*)\]\(([^)\s]+)\)/g;

async function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

async function loadImageForPdf(url) {
  const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
  if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);
  const blob = await response.blob();
  const rawDataUrl = await blobToDataUrl(blob);
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error('Image decode failed'));
    i.src = rawDataUrl;
  });
  const width = img.naturalWidth || 1;
  const height = img.naturalHeight || 1;

  // Re-encode through a canvas so any EXIF orientation the browser already
  // applied to the decoded image is baked into the pixel data — jsPDF's
  // addImage doesn't honor EXIF on its own, which would otherwise cause
  // phone photos to come out sideways.
  const canvas = window.document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext && canvas.getContext('2d');
  if (ctx && typeof canvas.toDataURL === 'function') {
    try {
      ctx.drawImage(img, 0, 0, width, height);
      const normalizedDataUrl = canvas.toDataURL('image/jpeg', 0.85);
      if (normalizedDataUrl && normalizedDataUrl.startsWith('data:image/')) {
        return { dataUrl: normalizedDataUrl, width, height, format: 'JPEG' };
      }
    } catch (err) {
      // Fall through to the raw data URL fallback (e.g., tainted canvas).
    }
  }

  const formatMatch = /^data:image\/([a-zA-Z0-9.+-]+)/.exec(rawDataUrl);
  let format = (formatMatch ? formatMatch[1] : 'jpeg').toUpperCase();
  if (format === 'JPG') format = 'JPEG';
  if (format === 'SVG+XML') format = 'SVG';
  return { dataUrl: rawDataUrl, width, height, format };
}

async function prefetchImages(markdown) {
  const urls = new Set();
  const text = markdown || '';
  let m;
  while ((m = IMAGE_GLOBAL_REGEX.exec(text)) !== null) {
    urls.add(m[2]);
  }
  const map = new Map();
  await Promise.all(
    Array.from(urls).map(async (url) => {
      try {
        map.set(url, await loadImageForPdf(url));
      } catch (err) {
        console.warn('Skipping image in PDF (failed to load):', url, err);
      }
    })
  );
  return map;
}

async function renderMarkdownToPdf(markdown) {
  const imageMap = await prefetchImages(markdown);

  const pdf = new jsPDF({ unit: 'pt', format: 'letter' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 54;
  const maxWidth = pageWidth - margin * 2;
  let y = margin;

  const ensureSpace = (lineHeight) => {
    if (y + lineHeight > pageHeight - margin) {
      pdf.addPage();
      y = margin;
    }
  };

  const stripInline = (text) =>
    text
      .replace(IMAGE_GLOBAL_REGEX, '')
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/\*(.+?)\*/g, '$1')
      .replace(/`(.+?)`/g, '$1');

  const writeWrapped = (text, { fontSize, fontStyle = 'normal', indent = 0, spacingBefore = 0, spacingAfter = 6 }) => {
    pdf.setFont('helvetica', fontStyle);
    pdf.setFontSize(fontSize);
    const lineHeight = fontSize * 1.3;
    if (spacingBefore && y > margin) {
      y += spacingBefore;
    }
    const lines = pdf.splitTextToSize(text, maxWidth - indent);
    lines.forEach((line) => {
      ensureSpace(lineHeight);
      pdf.text(line, margin + indent, y);
      y += lineHeight;
    });
    y += spacingAfter;
  };

  const renderImage = (alt, url) => {
    const data = imageMap.get(url);
    if (!data) {
      writeWrapped(`[Image unavailable: ${alt || url}]`, {
        fontSize: 10,
        fontStyle: 'italic',
        spacingAfter: 8,
      });
      return;
    }
    const aspect = data.height / data.width;
    let drawWidth = Math.min(maxWidth, 320);
    let drawHeight = drawWidth * aspect;
    const maxImageHeight = (pageHeight - margin * 2) * 0.6;
    if (drawHeight > maxImageHeight) {
      drawHeight = maxImageHeight;
      drawWidth = drawHeight / aspect;
    }
    if (y + drawHeight > pageHeight - margin) {
      pdf.addPage();
      y = margin;
    }
    try {
      pdf.addImage(data.dataUrl, data.format, margin, y, drawWidth, drawHeight);
      y += drawHeight + 8;
    } catch (err) {
      console.warn('addImage failed, embedding fallback text:', err);
      writeWrapped(`[Image unavailable: ${alt || url}]`, {
        fontSize: 10,
        fontStyle: 'italic',
        spacingAfter: 8,
      });
    }
  };

  const lines = (markdown || '').split('\n');
  let paragraph = [];

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    const joined = stripInline(paragraph.join(' ')).trim();
    if (joined) {
      writeWrapped(joined, { fontSize: 11, spacingAfter: 6 });
    }
    paragraph = [];
  };

  lines.forEach((rawLine) => {
    const line = rawLine.trimEnd();
    if (line.trim() === '') {
      flushParagraph();
      return;
    }
    const image = line.match(IMAGE_LINE_REGEX);
    const h1 = line.match(/^# (.+)/);
    const h2 = line.match(/^## (.+)/);
    const h3 = line.match(/^### (.+)/);
    const bullet = line.match(/^\s*[-*] (.+)/);
    const ordered = line.match(/^\s*\d+\.\s+(.+)/);
    if (image) {
      flushParagraph();
      renderImage(image[1], image[2]);
    } else if (h1) {
      flushParagraph();
      writeWrapped(stripInline(h1[1]), { fontSize: 20, fontStyle: 'bold', spacingBefore: 16, spacingAfter: 10 });
    } else if (h2) {
      flushParagraph();
      writeWrapped(stripInline(h2[1]), { fontSize: 15, fontStyle: 'bold', spacingBefore: 14, spacingAfter: 8 });
    } else if (h3) {
      flushParagraph();
      writeWrapped(stripInline(h3[1]), { fontSize: 12, fontStyle: 'bold', spacingBefore: 10, spacingAfter: 6 });
    } else if (bullet) {
      flushParagraph();
      writeWrapped(`•  ${stripInline(bullet[1])}`, { fontSize: 11, indent: 14, spacingAfter: 2 });
    } else if (ordered) {
      flushParagraph();
      writeWrapped(`•  ${stripInline(ordered[1])}`, { fontSize: 11, indent: 14, spacingAfter: 2 });
    } else {
      paragraph.push(line);
    }
  });
  flushParagraph();

  return pdf.output('blob');
}

function collectMarkdownImageUrls(markdown) {
  const urls = new Set();
  const text = markdown || '';
  for (const match of text.matchAll(IMAGE_GLOBAL_REGEX)) {
    if (match[2]) urls.add(match[2]);
  }
  return urls;
}

// When a Scope of Work is downloaded, surface every photo the user has
// included in the picker — even those the agent didn't embed in the
// markdown — so the PDF matches what's selected in the UI.
function augmentMarkdownWithScopePhotos(markdown, scopePhotos, excludedSet) {
  if (!Array.isArray(scopePhotos) || scopePhotos.length === 0) return markdown || '';
  const embedded = collectMarkdownImageUrls(markdown);
  const isIncluded = (p) => p && p.url && !excludedSet.has(p.url) && !embedded.has(p.url);
  const before = scopePhotos.filter((p) => p.kind === 'before' && isIncluded(p));
  const after = scopePhotos.filter((p) => p.kind === 'after' && isIncluded(p));
  if (before.length === 0 && after.length === 0) return markdown || '';

  const lines = [];
  if (markdown && !markdown.endsWith('\n')) lines.push('');
  lines.push('', '## Reference Photos', '');
  const renderGroup = (heading, photos) => {
    if (photos.length === 0) return;
    lines.push(`### ${heading}`, '');
    photos.forEach((p) => {
      const alt = p.caption || heading;
      lines.push(`![${alt}](${p.url})`);
      if (p.caption) lines.push(`*${p.caption}*`);
      lines.push('');
    });
  };
  renderGroup('Before', before);
  renderGroup('After', after);
  return `${markdown || ''}${lines.join('\n')}`;
}

function formatScopeOfWorkAsMarkdown(scope) {
  if (!scope) return '';
  const lines = [];
  lines.push(`# ${scope.project_name || 'Scope of Work'}`, '');
  if (scope.summary) {
    lines.push('## Summary', '', scope.summary, '');
  }
  (scope.sections || []).forEach((section) => {
    lines.push(`## ${section.category}`, '');
    if (section.scope_text) lines.push(section.scope_text, '');
    if (Array.isArray(section.materials_list) && section.materials_list.length > 0) {
      lines.push('**Materials:**');
      section.materials_list.forEach((m) => {
        const qty = m.quantity ? ` — ${m.quantity}` : '';
        const notes = m.notes ? ` (${m.notes})` : '';
        lines.push(`- ${m.item}${qty}${notes}`);
      });
      lines.push('');
    }
    if (section.labor_notes) {
      lines.push(`**Labor notes:** ${section.labor_notes}`, '');
    }
  });
  if (Array.isArray(scope.exclusions) && scope.exclusions.length > 0) {
    lines.push('## Exclusions', '');
    scope.exclusions.forEach((e) => lines.push(`- ${e}`));
    lines.push('');
  }
  if (Array.isArray(scope.assumptions) && scope.assumptions.length > 0) {
    lines.push('## Assumptions', '');
    scope.assumptions.forEach((a) => lines.push(`- ${a}`));
    lines.push('');
  }
  if (scope.timeline_notes) {
    lines.push('## Timeline', '', scope.timeline_notes, '');
  }
  if (Array.isArray(scope.special_conditions) && scope.special_conditions.length > 0) {
    lines.push('## Special Conditions', '');
    scope.special_conditions.forEach((c) => lines.push(`- ${c}`));
    lines.push('');
  }
  return lines.join('\n');
}

function DocumentPanel({
  document,
  scopeOfWork = null,
  documents = [],
  messages = [],
  scopePhotos = [],
  scopePhotoExcluded = [],
  onScopePhotoToggle,
  canEditScopePhotos = true,
  uid = null,
  isActive = true,
}) {
  const [subTab, setSubTab] = useState('documents');
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [photoPickerOpen, setPhotoPickerOpen] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState(null);

  useEffect(() => {
    if (!isActive) return;
    trackPageView(`${window.location.pathname.replace(/\/$/, '')}/${subTab}`);
  }, [subTab, isActive]);

  const excludedSet = useMemo(() => new Set(scopePhotoExcluded), [scopePhotoExcluded]);
  const beforePhotos = useMemo(() => scopePhotos.filter((p) => p.kind === 'before'), [scopePhotos]);
  const afterPhotos = useMemo(() => scopePhotos.filter((p) => p.kind === 'after'), [scopePhotos]);
  const includedCount = useMemo(
    () => scopePhotos.filter((p) => !excludedSet.has(p.url)).length,
    [scopePhotos, excludedSet]
  );

  // Build the unified document list. The Scope of Work appears as a special
  // pseudo-document so it lives alongside custom user-requested documents
  // in a single grid; per-doc affordances (e.g. the photo picker) live
  // inside that doc's detail view rather than at the panel level.
  const sowDoc = useMemo(() => {
    if (scopeOfWork) {
      return {
        id: SOW_DOC_ID,
        title: 'Scope of Work',
        kind: 'scope_of_work',
        content: formatScopeOfWorkAsMarkdown(scopeOfWork),
        isScopeOfWork: true,
      };
    }
    if (document) {
      return {
        id: SOW_DOC_ID,
        title: 'Scope of Work',
        kind: 'scope_of_work',
        content: document,
        isScopeOfWork: true,
      };
    }
    return null;
  }, [scopeOfWork, document]);

  const allDocs = useMemo(() => {
    const arr = [];
    if (sowDoc) arr.push(sowDoc);
    arr.push(...(documents || []));
    return arr;
  }, [sowDoc, documents]);

  const selectedDoc = useMemo(
    () => allDocs.find((d) => d && d.id === selectedDocId) || null,
    [allDocs, selectedDocId]
  );

  const displayContent = selectedDoc ? selectedDoc.content : '';

  const downloadFilename = useMemo(() => {
    if (!selectedDoc) return 'document.pdf';
    if (selectedDoc.isScopeOfWork) return 'scope-of-work.pdf';
    const safe = (selectedDoc.title || 'document')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return `${safe || 'document'}.pdf`;
  }, [selectedDoc]);

  const handlePhotoToggle = (url) => {
    if (!canEditScopePhotos || !onScopePhotoToggle) return;
    onScopePhotoToggle(url);
    trackEvent('scope_photo_toggled', { included: excludedSet.has(url) });
  };

  const handleCopy = () => {
    if (displayContent) {
      navigator.clipboard.writeText(displayContent);
      trackEvent('document_copied');
    }
  };

  const handleDownload = async () => {
    if (!displayContent) return;
    const docKind = selectedDoc?.kind || (selectedDoc?.isScopeOfWork ? 'scope_of_work' : 'custom');
    trackEvent('document_downloaded', { kind: docKind });
    if (uid) {
      trackFirstTime('first_document_downloaded', `download:${uid}`, { kind: docKind });
    }
    const contentForPdf = augmentMarkdownWithScopePhotos(
      displayContent,
      selectedDoc?.isScopeOfWork ? scopePhotos : [],
      excludedSet
    );
    const blob = await renderMarkdownToPdf(contentForPdf);
    const url = URL.createObjectURL(blob);
    const a = window.document.createElement('a');
    a.href = url;
    a.download = downloadFilename;
    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Extract all uploaded files from messages
  const uploadedFiles = useMemo(() => {
    const files = [];
    const seen = new Set();
    messages.forEach((msg) => {
      if (msg.images) {
        let imgs;
        try {
          imgs = typeof msg.images === 'string' ? JSON.parse(msg.images) : msg.images;
        } catch {
          return;
        }
        imgs.forEach((img) => {
          if (img.url && !seen.has(img.url)) {
            seen.add(img.url);
            files.push({
              url: img.url,
              name: img.name || 'Untitled',
              type: img.type || 'image/jpeg',
              label: img.label,
            });
          }
        });
      }
    });
    return files;
  }, [messages]);

  const imageFiles = uploadedFiles.filter(f => f.type && f.type.startsWith('image/'));
  const documentFiles = uploadedFiles.filter(f => f.type === 'application/pdf');
  const linkFiles = uploadedFiles.filter(f => f.type === 'link');

  // Scope of Work is always pinned first; the rest sort by most-recently-updated.
  const sortedDocs = useMemo(() => {
    const others = (documents || [])
      .slice()
      .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
    return sowDoc ? [sowDoc, ...others] : others;
  }, [sowDoc, documents]);

  const showSowPhotoPicker = selectedDoc && selectedDoc.isScopeOfWork && scopePhotos.length > 0;

  return (
    <div className="document-panel d-flex flex-column h-100">
      {/* Sub-tabs */}
      <div className="document-sub-tabs d-flex gap-1 px-3 pt-2 pb-1 border-bottom bg-white">
        <button
          className={`design-sub-tab ${subTab === 'documents' ? 'active' : ''}`}
          onClick={() => { setSubTab('documents'); }}
        >
          <i className="bi bi-files me-1"></i>
          Documents
          {allDocs.length > 0 && <span className="badge bg-secondary ms-1">{allDocs.length}</span>}
        </button>
        <button
          className={`design-sub-tab ${subTab === 'uploads' ? 'active' : ''}`}
          onClick={() => setSubTab('uploads')}
        >
          <i className="bi bi-cloud-upload me-1"></i>
          Uploads
          {uploadedFiles.length > 0 && <span className="badge bg-secondary ms-1">{uploadedFiles.length}</span>}
        </button>
      </div>

      <div className="document-panel-content flex-grow-1 overflow-auto">
        {/* Documents sub-tab — unified grid + per-doc detail view */}
        {subTab === 'documents' && (
          <>
            {selectedDoc ? (
              <div className="documents-tab d-flex flex-column">
                <div className="d-flex align-items-center px-3 pt-3 pb-2">
                  <h5 className="mb-0 flex-grow-1 text-truncate">{selectedDoc.title}</h5>
                  {selectedDoc.kind && !selectedDoc.isScopeOfWork && (
                    <span className="badge bg-light text-dark ms-2">{selectedDoc.kind}</span>
                  )}
                  <div className="d-flex gap-1 ms-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleCopy}
                      title="Copy"
                      disabled={!displayContent}
                    >
                      <i className="bi bi-clipboard"></i>
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleDownload}
                      title="Download"
                      disabled={!displayContent}
                    >
                      <i className="bi bi-download"></i>
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setSelectedDocId(null)}
                      title="Close document"
                    >
                      <i className="bi bi-x-lg"></i>
                    </Button>
                  </div>
                </div>

                {/* Per-doc management for the Scope of Work — photo picker */}
                {showSowPhotoPicker && (
                  <div className="scope-photo-picker border-top border-bottom bg-light">
                    <button
                      type="button"
                      className="scope-photo-picker-toggle w-100 d-flex align-items-center justify-content-between px-3 py-2 bg-transparent border-0"
                      onClick={() => setPhotoPickerOpen((v) => !v)}
                      aria-expanded={photoPickerOpen}
                    >
                      <span className="d-flex align-items-center">
                        <i className="bi bi-images me-2"></i>
                        <span className="fw-semibold">Photos in Document</span>
                        <span className="badge bg-secondary ms-2">
                          {includedCount} of {scopePhotos.length}
                        </span>
                      </span>
                      <i className={`bi bi-chevron-${photoPickerOpen ? 'up' : 'down'}`}></i>
                    </button>
                    {photoPickerOpen && (
                      <div className="px-3 pb-3">
                        {!canEditScopePhotos && (
                          <div className="small text-muted mb-2">
                            <i className="bi bi-info-circle me-1"></i>
                            You don't have permission to change which photos are included.
                          </div>
                        )}
                        {beforePhotos.length > 0 && (
                          <div className="mb-3">
                            <div className="small text-muted mb-2">Before</div>
                            <Row>
                              {beforePhotos.map((photo, idx) => {
                                const included = !excludedSet.has(photo.url);
                                return (
                                  <Col xs={6} md={4} key={`before-${idx}`} className="mb-2">
                                    <Card className={`scope-photo-card h-100 ${included ? '' : 'scope-photo-card-excluded'}`}>
                                      <div
                                        className="scope-photo-image-wrapper position-relative"
                                        onClick={() => setFullscreenImage({ url: photo.url, name: photo.caption })}
                                      >
                                        <Card.Img
                                          variant="top"
                                          src={photo.url}
                                          alt={photo.caption || 'Before photo'}
                                          className="scope-photo-image"
                                          style={{ opacity: included ? 1 : 0.45 }}
                                        />
                                      </div>
                                      <Card.Body className="p-2 d-flex align-items-center justify-content-between">
                                        <small className="text-muted text-truncate me-2">{photo.caption || 'Before'}</small>
                                        <Form.Check
                                          type="switch"
                                          id={`scope-photo-before-${idx}`}
                                          checked={included}
                                          disabled={!canEditScopePhotos}
                                          onChange={() => handlePhotoToggle(photo.url)}
                                          title={included ? 'Included in document' : 'Excluded from document'}
                                        />
                                      </Card.Body>
                                    </Card>
                                  </Col>
                                );
                              })}
                            </Row>
                          </div>
                        )}
                        {afterPhotos.length > 0 && (
                          <div>
                            <div className="small text-muted mb-2">After</div>
                            <Row>
                              {afterPhotos.map((photo, idx) => {
                                const included = !excludedSet.has(photo.url);
                                return (
                                  <Col xs={6} md={4} key={`after-${idx}`} className="mb-2">
                                    <Card className={`scope-photo-card h-100 ${included ? '' : 'scope-photo-card-excluded'}`}>
                                      <div
                                        className="scope-photo-image-wrapper position-relative"
                                        onClick={() => setFullscreenImage({ url: photo.url, name: photo.caption })}
                                      >
                                        <Card.Img
                                          variant="top"
                                          src={photo.url}
                                          alt={photo.caption || 'After photo'}
                                          className="scope-photo-image"
                                          style={{ opacity: included ? 1 : 0.45 }}
                                        />
                                      </div>
                                      <Card.Body className="p-2 d-flex align-items-center justify-content-between">
                                        <small className="text-muted text-truncate me-2">{photo.caption || 'After'}</small>
                                        <Form.Check
                                          type="switch"
                                          id={`scope-photo-after-${idx}`}
                                          checked={included}
                                          disabled={!canEditScopePhotos}
                                          onChange={() => handlePhotoToggle(photo.url)}
                                          title={included ? 'Included in document' : 'Excluded from document'}
                                        />
                                      </Card.Body>
                                    </Card>
                                  </Col>
                                );
                              })}
                            </Row>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="scope-document-body p-3">
                  {displayContent ? (
                    <ReactMarkdown>{displayContent}</ReactMarkdown>
                  ) : (
                    <div className="text-muted text-center mt-5">
                      <i className="bi bi-file-earmark-text display-4 d-block mb-3 opacity-25"></i>
                      <p>This document is empty.</p>
                    </div>
                  )}
                </div>
              </div>
            ) : allDocs.length === 0 ? (
              <div className="documents-tab p-3">
                <div className="text-muted text-center mt-5">
                  <i className="bi bi-files display-4 d-block mb-3 opacity-25"></i>
                  <p>No documents yet.</p>
                  <p className="small">
                    Ask the assistant for any document — a contractor scope of work,
                    a step-by-step DIY plan, a punch list, or anything else.
                    It will appear here as a thumbnail you can open.
                  </p>
                </div>
              </div>
            ) : (
              <div className="documents-tab p-3">
                <Row>
                  {sortedDocs.map((doc) => (
                    <Col xs={12} sm={6} md={4} key={doc.id} className="mb-3">
                      <Card
                        className="document-thumbnail-card h-100"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedDocId(doc.id)}
                        title={doc.title}
                      >
                        <Card.Body className="d-flex flex-column">
                          <div className="d-flex align-items-start mb-2">
                            <i
                              className={`${doc.isScopeOfWork ? 'bi bi-file-earmark-ruled' : 'bi bi-file-earmark-text'} text-primary me-2`}
                              style={{ fontSize: '1.5rem' }}
                            ></i>
                            <div className="flex-grow-1 min-w-0">
                              <div
                                className="fw-semibold"
                                style={{
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {doc.title || 'Untitled'}
                              </div>
                              {!doc.isScopeOfWork && doc.kind ? (
                                <span className="badge bg-light text-dark mt-1" style={{ fontSize: '0.65rem' }}>
                                  {doc.kind}
                                </span>
                              ) : null}
                            </div>
                          </div>
                          <div
                            className="text-muted small flex-grow-1"
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 4,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {(doc.content || '').replace(/[#*_`>-]/g, '').trim().slice(0, 240)}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>
            )}
          </>
        )}

        {/* Uploads sub-tab */}
        {subTab === 'uploads' && (
          <div className="uploads-tab p-3">
            {uploadedFiles.length === 0 ? (
              <div className="text-muted text-center mt-5">
                <i className="bi bi-cloud-upload display-4 d-block mb-3 opacity-25"></i>
                <p>Files you upload in the chat will appear here.</p>
              </div>
            ) : (
              <>
                {/* Images */}
                {imageFiles.length > 0 && (
                  <div className="mb-4">
                    <h6 className="text-muted mb-3">
                      <i className="bi bi-image me-2"></i>Images ({imageFiles.length})
                    </h6>
                    <Row>
                      {imageFiles.map((file, idx) => (
                        <Col xs={6} md={4} key={`img-${idx}`} className="mb-3">
                          <Card className="upload-item-card h-100">
                            <div
                              className="upload-item-image-wrapper"
                              onClick={() => setFullscreenImage({ url: file.url, name: file.name })}
                            >
                              <Card.Img variant="top" src={file.url} alt={file.name} className="upload-item-image" />
                            </div>
                            <Card.Body className="p-2">
                              <small className="text-muted text-truncate d-block">{file.name}</small>
                              {file.label && (
                                <span className="badge bg-light text-dark mt-1" style={{ fontSize: '0.65rem' }}>
                                  {file.label === 'current' ? 'Current Space' :
                                   file.label === 'inspiration' ? 'Inspiration' :
                                   file.label === 'product' ? 'Product' :
                                   file.label === 'mood_board' ? 'Mood Board' : file.label}
                                </span>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}

                {/* Documents/PDFs */}
                {documentFiles.length > 0 && (
                  <div className="mb-4">
                    <h6 className="text-muted mb-3">
                      <i className="bi bi-file-earmark-pdf me-2"></i>Documents ({documentFiles.length})
                    </h6>
                    <Row>
                      {documentFiles.map((file, idx) => (
                        <Col xs={6} md={4} key={`doc-${idx}`} className="mb-3">
                          <Card
                            className="upload-item-card h-100"
                            style={{ cursor: 'pointer' }}
                            onClick={() => window.open(file.url, '_blank', 'noopener,noreferrer')}
                          >
                            <div className="upload-item-pdf-wrapper d-flex align-items-center justify-content-center">
                              <i className="bi bi-file-earmark-pdf-fill text-danger" style={{ fontSize: '2.5rem' }}></i>
                            </div>
                            <Card.Body className="p-2">
                              <small className="text-muted text-truncate d-block">{file.name}</small>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}

                {/* Links */}
                {linkFiles.length > 0 && (
                  <div className="mb-4">
                    <h6 className="text-muted mb-3">
                      <i className="bi bi-link-45deg me-2"></i>Links ({linkFiles.length})
                    </h6>
                    <Row>
                      {linkFiles.map((file, idx) => (
                        <Col xs={12} md={6} key={`link-${idx}`} className="mb-2">
                          <a
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="d-flex align-items-center p-2 rounded text-decoration-none upload-link-item"
                          >
                            <i className="bi bi-link-45deg me-2 text-primary"></i>
                            <small className="text-truncate">{file.name}</small>
                            <i className="bi bi-box-arrow-up-right ms-auto text-muted" style={{ fontSize: '0.7rem' }}></i>
                          </a>
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Fullscreen Image Modal */}
      <Modal
        show={!!fullscreenImage}
        onHide={() => setFullscreenImage(null)}
        centered
        size="xl"
        className="fullscreen-image-modal"
      >
        <Modal.Header closeButton className="border-0 position-absolute top-0 end-0" style={{ zIndex: 1 }}>
        </Modal.Header>
        <Modal.Body className="p-0 d-flex align-items-center justify-content-center bg-dark">
          {fullscreenImage && (
            <img
              src={fullscreenImage.url}
              alt={fullscreenImage.name || 'Full size image'}
              style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain' }}
            />
          )}
        </Modal.Body>
        {fullscreenImage?.name && (
          <Modal.Footer className="bg-dark text-white border-0 justify-content-center">
            <small>{fullscreenImage.name}</small>
          </Modal.Footer>
        )}
      </Modal>
    </div>
  );
}

export default DocumentPanel;
